import type { IGif } from '@giphy/js-types';

const MAX_GIF_BYTES = 10 * 1024 * 1024;

const pickRendition = (gif: IGif): { url: string; size: number } | null => {
    const images = gif.images;
    const candidates = [
        images.downsized,
        images.downsized_medium,
        images.fixed_height,
        images.original,
    ];

    for (const item of candidates) {
        const url = item?.url;
        if (!url) {
            continue;
        }
        const size = Number(item.size || 0);
        if (!size || size <= MAX_GIF_BYTES) {
            return { url, size };
        }
    }

    return images.downsized?.url
        ? { url: images.downsized.url, size: Number(images.downsized.size || 0) }
        : null;
};

export const isAllowedGiphyMediaUrl = (raw: string): boolean => {
    try {
        const parsed = new URL(raw);
        if (parsed.protocol !== 'https:') {
            return false;
        }
        const host = parsed.hostname.toLowerCase();
        return host === 'giphy.com' || host.endsWith('.giphy.com');
    } catch {
        return false;
    }
};

export const giphyToFile = async (gif: IGif): Promise<File> => {
    const picked = pickRendition(gif);
    if (!picked?.url || !isAllowedGiphyMediaUrl(picked.url)) {
        throw new Error('Не удалось получить GIF из GIPHY');
    }
    if (picked.size > MAX_GIF_BYTES) {
        throw new Error('GIF слишком большой. Максимум 10MB');
    }

    const proxyUrl = `/api/tasks/giphy-media?url=${encodeURIComponent(picked.url)}`;
    const response = await fetch(proxyUrl);
    if (!response.ok) {
        throw new Error('Не удалось скачать GIF');
    }

    const blob = await response.blob();
    if (blob.size > MAX_GIF_BYTES) {
        throw new Error('GIF слишком большой. Максимум 10MB');
    }

    const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/gif';
    const name = `giphy-${gif.id}.gif`;
    return new File([blob], name, { type });
};
